/*
 * Static content for legal pages. Written in a neutral, professional voice.
 * Contact details reflect the APEX Innovation Solutions brand.
 */

const CONTACT_BLOCK = {
  business_name: "APEX Innovation Solutions",
  legal_name_in: "APEX Innovation Solutions",
  legal_name_us: "APEX Innovation Solutions LLC",
  founder: "Jaswinder Singh",
  // India
  registration_no_in: "SCG126021000459541",
  address_in_line1: "2nd Floor, 113, Shri Kuber Complex",
  address_in_line2: "Near Indo Canadian Bus Services",
  address_in_city: "Jalandhar",
  address_in_state: "Punjab",
  address_in_postal: "144001",
  address_in_country: "India",
  // USA
  entity_no_us: "14712993-0160",
  state_us: "Utah",
  address_us_line1: "7533 S Center View Ct, Ste 210",
  address_us_city: "West Jordan",
  address_us_state: "UT",
  address_us_postal: "84084",
  address_us_country: "USA",
  // Contact
  business_email: "info@apexinnovationsolutions.com",
  phone: "+91 77197 79448",
  phone_tel: "+917719779448",
  website: "https://apexinnovationsolutions.com",
  // Back-compat
  registration_no: "SCG126021000459541",
};

export const LEGAL_CONTENT = {
  privacy: {
    slug: "privacy",
    title: "Privacy Policy",
    subtitle: "How APEX Innovation Solutions collects, uses, and protects your information.",
    updated: "December 2025",
    sections: [
      {
        heading: "1. Introduction",
        body:
          "APEX Innovation Solutions and its US affiliate APEX Innovation Solutions LLC (collectively 'APEX', 'we', 'our', 'us') respect your privacy. This Privacy Policy explains what personal information we collect when you use our website (" +
          CONTACT_BLOCK.website +
          "), how we use it, and the rights you have over it. The business is founded and led by Mr. Jaswinder Singh.",
      },
      {
        heading: "2. Information we collect",
        body:
          "We collect information you provide directly to us — such as your name, business email, phone number, company name, budget range, service interest, and project message — when you submit our contact form or otherwise reach out. We also collect limited technical data such as IP address, browser type, device information, referring URL, and pages visited, through cookies and analytics tools.",
      },
      {
        heading: "3. How we use your information",
        body:
          "We use your information to (a) respond to enquiries and provide the services you request, (b) send you proposals, quotes, and project updates, (c) improve our website, marketing, and services, (d) comply with legal obligations, and (e) prevent fraud or abuse.",
      },
      {
        heading: "4. Sharing your information",
        body:
          "We do not sell your personal information. We share it only with trusted service providers who help us operate our business (email delivery, hosting, analytics), or when required by law. All processors are bound by confidentiality obligations.",
      },
      {
        heading: "5. Cookies and analytics",
        body:
          "Our website uses cookies and similar technologies to remember preferences, measure traffic, and improve performance. You can control cookies through your browser settings. See our Cookie Policy for details.",
      },
      {
        heading: "6. Data retention",
        body:
          "We retain personal information only for as long as necessary to fulfil the purposes described in this policy, meet legal or regulatory requirements, or resolve disputes.",
      },
      {
        heading: "7. Your rights",
        body:
          "Depending on where you live, you may have the right to access, correct, delete, restrict, or port your personal information, and to withdraw consent or object to processing. To exercise any of these rights, email us at " +
          CONTACT_BLOCK.business_email +
          ".",
      },
      {
        heading: "8. Security",
        body:
          "We use reasonable administrative, technical, and physical safeguards to protect your information. No method of transmission over the internet is 100% secure, but we work to protect your data against unauthorized access, alteration, disclosure, or destruction.",
      },
      {
        heading: "9. Children's privacy",
        body:
          "Our services are not directed to children under 16, and we do not knowingly collect personal information from them. If you believe a child has provided us data, please contact us and we will delete it promptly.",
      },
      {
        heading: "10. Changes to this policy",
        body:
          "We may update this Privacy Policy from time to time. Material changes will be posted on this page with a revised 'Last updated' date.",
      },
      {
        heading: "11. Contact",
        body:
          "For any privacy questions or requests, contact APEX Innovation Solutions (Founder: Mr. Jaswinder Singh) at " +
          CONTACT_BLOCK.business_email +
          " or " +
          CONTACT_BLOCK.phone +
          ".",
      },
    ],
  },

  terms: {
    slug: "terms",
    title: "Terms & Conditions",
    subtitle: "The terms that govern your use of the APEX website and our services.",
    updated: "December 2025",
    sections: [
      {
        heading: "1. Acceptance of terms",
        body:
          "By accessing or using " +
          CONTACT_BLOCK.website +
          " or engaging APEX Innovation Solutions (and, where applicable, its US affiliate APEX Innovation Solutions LLC — Utah entity 14712993-0160, principal Mr. Jaswinder Singh) for any service, you agree to these Terms & Conditions. If you do not agree, please do not use our website or services.",
      },
      {
        heading: "2. Services",
        body:
          "APEX provides web design, SEO, AEO/GEO, landing page design, branding, content strategy, performance tracking, and conversion optimization services. Scope, deliverables, timelines, and pricing for each engagement are documented separately in a written proposal or statement of work agreed by both parties.",
      },
      {
        heading: "3. Client responsibilities",
        body:
          "You agree to provide accurate, timely information, feedback, approvals, and any content, assets, or access we reasonably require to deliver the services. Delays caused by missing information may impact timelines and costs.",
      },
      {
        heading: "4. Payment terms",
        body:
          "Fees, deposits, milestones, and payment schedules are set out in your project proposal. Unless otherwise agreed in writing, invoices are due within 15 days of the invoice date. Late payments may incur reasonable interest and pause active work.",
      },
      {
        heading: "5. Intellectual property",
        body:
          "Upon full payment, you own the final deliverables produced specifically for you. APEX retains ownership of our pre-existing tools, frameworks, code libraries, and internal know-how. We may showcase completed work in our portfolio unless you request otherwise in writing.",
      },
      {
        heading: "6. Third-party services",
        body:
          "Projects may involve third-party platforms (hosting, CMSs, analytics, plugins). Their terms and pricing apply and are not controlled by APEX. We are not responsible for outages or policy changes on third-party services.",
      },
      {
        heading: "7. Warranties and disclaimers",
        body:
          "We deliver services with professional care and skill. However, we do not guarantee specific business outcomes such as revenue, rankings, or traffic — these depend on many factors outside our control. All services and website content are provided 'as is' to the maximum extent permitted by law.",
      },
      {
        heading: "8. Limitation of liability",
        body:
          "To the fullest extent permitted by law, APEX's total liability for any claim arising from a project is limited to the fees you paid us for the affected service in the 3 months prior to the claim. We are not liable for indirect, incidental, or consequential damages.",
      },
      {
        heading: "9. Termination",
        body:
          "Either party may terminate an active engagement with written notice as defined in the proposal. Fees for work already completed remain payable. Confidentiality and IP terms survive termination.",
      },
      {
        heading: "10. Governing law",
        body:
          "These Terms are governed by the laws of India for engagements contracted with APEX Innovation Solutions, and the laws of the State of Utah, USA for engagements contracted with APEX Innovation Solutions LLC. Any disputes will be subject to the exclusive jurisdiction of the courts in the applicable country, unless the engagement contract specifies otherwise.",
      },
      {
        heading: "11. Authorized signatory",
        body:
          "Mr. Jaswinder Singh is the authorized signatory and founder of APEX Innovation Solutions (India) and APEX Innovation Solutions LLC (Utah, USA). All contracts, proposals, and formal communications on behalf of the business are executed by or under his authority.",
      },
      {
        heading: "12. Contact",
        body:
          "Questions about these Terms? Email " + CONTACT_BLOCK.business_email + " or call " + CONTACT_BLOCK.phone + ".",
      },
    ],
  },

  refund: {
    slug: "refund",
    title: "Refund Policy",
    subtitle: "Our approach to refunds, cancellations, and revisions.",
    updated: "December 2025",
    sections: [
      {
        heading: "1. Nature of our services",
        body:
          "APEX Innovation Solutions delivers custom, project-based professional services. Because significant strategy, design, and development time is committed upfront, we cannot offer blanket refunds after work has begun. However, we care about client satisfaction and handle each request fairly.",
      },
      {
        heading: "2. Deposits and milestone payments",
        body:
          "Project deposits (typically 30–50%) are non-refundable once discovery or design work has started, as they reserve our team's capacity. Milestone payments are non-refundable for work already completed and approved. Any prepaid, unstarted milestones may be refunded on written request.",
      },
      {
        heading: "3. Revisions",
        body:
          "Every package includes a defined number of revision rounds. Additional revisions are billed at the rates listed in your proposal. We will not withhold refunds in exchange for scope beyond the agreed contract.",
      },
      {
        heading: "4. Cancellation by client",
        body:
          "If you cancel a project mid-way, you will be invoiced for all work completed up to the cancellation date. Any funds paid beyond that value will be refunded within 15 business days.",
      },
      {
        heading: "5. Cancellation by APEX",
        body:
          "If we cancel a project due to reasons on our side (excluding client non-cooperation), we will refund any prepaid amount for work not yet delivered.",
      },
      {
        heading: "6. Monthly retainers",
        body:
          "Retainer agreements can be paused or terminated with 15 days' written notice. Fees for the current month are non-refundable but any prepaid future months will be refunded pro rata.",
      },
      {
        heading: "7. Chargebacks",
        body:
          "Please reach out to us before initiating a chargeback — we resolve billing disputes directly and quickly. Unwarranted chargebacks may result in additional recovery fees.",
      },
      {
        heading: "8. How to request a refund",
        body:
          "Send a written refund request to " +
          CONTACT_BLOCK.business_email +
          " including your invoice number and reason. We respond within 3 business days and process approved refunds within 15 business days to the original payment method.",
      },
    ],
  },

  cookies: {
    slug: "cookies",
    title: "Cookie Policy",
    subtitle: "How and why we use cookies on the APEX website.",
    updated: "December 2025",
    sections: [
      {
        heading: "1. What are cookies?",
        body:
          "Cookies are small text files stored on your device by websites you visit. They help sites remember information, understand usage, and improve your experience.",
      },
      {
        heading: "2. Cookies we use",
        body:
          "APEX uses (a) strictly necessary cookies required for the site to function, (b) preference cookies to remember choices like currency (USD/INR) or theme, and (c) analytics cookies to measure aggregate traffic and improve content. We do not use advertising or profiling cookies without your consent.",
      },
      {
        heading: "3. Third-party cookies",
        body:
          "Some cookies are set by third-party tools we use (for example, analytics or embedded content). These providers have their own privacy and cookie policies which govern their use.",
      },
      {
        heading: "4. Managing cookies",
        body:
          "You can control cookies through your browser settings — most browsers let you view, delete, or block cookies. Blocking necessary cookies may cause parts of the site to break.",
      },
      {
        heading: "5. Contact",
        body:
          "Cookie-related questions? Email " + CONTACT_BLOCK.business_email + ".",
      },
    ],
  },

  disclaimer: {
    slug: "disclaimer",
    title: "Disclaimer",
    subtitle: "Important notices about the information on this website.",
    updated: "December 2025",
    sections: [
      {
        heading: "1. General information",
        body:
          "The content on " +
          CONTACT_BLOCK.website +
          " is provided for general informational purposes only. While we work hard to keep information accurate and up to date, we make no representations or warranties of any kind about completeness, accuracy, reliability, or availability.",
      },
      {
        heading: "2. No professional advice",
        body:
          "Nothing on this website constitutes legal, financial, tax, or medical advice. You should consult qualified professionals before acting on any information found here.",
      },
      {
        heading: "3. External links",
        body:
          "Our website may link to external websites we do not control. We are not responsible for the content, accuracy, or practices of any linked third-party site.",
      },
      {
        heading: "4. Marketing claims",
        body:
          "Any statistics, results, or testimonials shown on our website reflect specific client engagements and do not guarantee similar outcomes for your business.",
      },
      {
        heading: "5. Limitation of liability",
        body:
          "APEX Innovation Solutions is not liable for any loss or damage arising from your reliance on the information published on this website.",
      },
    ],
  },
};

export const LEGAL_INDEX = [
  { slug: "privacy", title: "Privacy Policy" },
  { slug: "terms", title: "Terms & Conditions" },
  { slug: "refund", title: "Refund Policy" },
  { slug: "cookies", title: "Cookie Policy" },
  { slug: "disclaimer", title: "Disclaimer" },
];

export { CONTACT_BLOCK };
